import { ReactNode, useEffect, useState } from "react";
import styled from "styled-components/macro";
import InlineWarning from "../components/InlineWarning";
import IconButton from "../components/IconButton";
import ModelOutageMessage from "../components/ModelOutageMessage";
import { CONTENT_GENERATION_FAILED_MESSAGE, CONTENT_POLICY_MESSAGE } from "../lib/Errors";

const Content = styled.div`
  min-width: 0;
  width: 100%;
  text-align: left;
  text-transform: none;
  letter-spacing: normal;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--danger-color);

  .error-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .support-link {
    display: inline-block;
    margin-top: 10px;
    color: inherit;
    text-decoration: underline;
  }

  .activity {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid var(--grey-3);
  }

  .activity-details {
    min-width: 0;
    flex: 1;
    font-size: 12px;
  }

  .activity-id {
    color: inherit;
    user-select: text;
    overflow-wrap: anywhere;
  }

  .copy-status {
    margin-top: 6px;
    font-size: 12px;
  }
`;

type ErrorKind = "generic" | "content-policy" | "generation-failed" | "model-outage" | "delete-failed" | "sprint";

export default function CardSetError({ kind, sprintMessage, activityId, supportUrl }: { kind: ErrorKind; sprintMessage?: string | null; activityId?: string; supportUrl: string }) {
  const [copyStatus, setCopyStatus] = useState("");

  useEffect(() => {
    setCopyStatus("");
  }, [activityId]);

  const copyActivityId = async () => {
    if (!activityId) return;
    try {
      await navigator.clipboard.writeText(activityId);
      setCopyStatus("Activity ID copied.");
    } catch {
      setCopyStatus("Couldn’t copy. Select and copy the Activity ID above.");
    }
  };

  const messages: Record<ErrorKind, ReactNode> = {
    sprint: sprintMessage || "Free sprint access is unavailable. Please refresh and try again.",
    "content-policy": CONTENT_POLICY_MESSAGE,
    "generation-failed": CONTENT_GENERATION_FAILED_MESSAGE,
    "delete-failed": "Could not delete this card",
    "model-outage": <ModelOutageMessage supportUrl={supportUrl} />,
    generic: (
      <>
        <div className="error-title">We couldn’t complete this request</div>
        <div>
          Please try again. If the problem continues, contact support
          {activityId ? " and include the Activity ID below." : "."}
        </div>
      </>
    ),
  };

  return (
    // Card headers collapse on click and prevent link navigation. Keep these
    // controls and text selection independent of that parent interaction.
    <div onClick={(event) => event.stopPropagation()}>
      <InlineWarning>
        <Content>
          <div role="alert">{messages[kind]}</div>
          {kind !== "content-policy" && kind !== "model-outage" && (
            <a className="support-link" href={supportUrl} target="_blank" rel="noopener noreferrer">
              Contact support
            </a>
          )}
          {activityId && (
            <>
              <div className="activity">
                <div className="activity-details">
                  <div>Activity ID</div>
                  <span className="activity-id">{activityId}</span>
                </div>
                <IconButton
                  src="/assets/copy2.svg"
                  size={32}
                  iconSize={22}
                  ariaLabel="Copy Activity ID"
                  hoverTipText="Copy Activity ID"
                  onClick={copyActivityId}
                />
              </div>
              <div className="copy-status" role="status">{copyStatus}</div>
            </>
          )}
        </Content>
      </InlineWarning>
    </div>
  );
}
